import axios from 'axios';
import { orderBy, filterBy, groupBy, GroupDescriptor } from '@progress/kendo-data-query';
import { toasterAlert } from '../redux/toasterSlice';
import { store } from '../store';
import { ROLES } from './Constant';
import { setAttendantUserSummary } from '../redux/globalSlice';

const getUserToken = () => {
   return localStorage.getItem('RPMAuthToken') ?? null
}

const logOut = () => {
  localStorage.removeItem('RPMAuthToken')
  window.location.reload();
}

const getAttendantUserSummary = async (cancelToken?: any) => {
  const roles = (store.getState().global.authUserAllData as { roles?: string[] })?.roles;
  if (!roles?.includes(ROLES.ATTENDANT)) {
    return;
  }
  return axios.get(`${process.env.REACT_APP_API_URL}api/dashboard/top-metrics`, {
    cancelToken: cancelToken
  }).then((response: any) => {
    store.dispatch(setAttendantUserSummary(response));
  }).catch((e: any) =>
    store.dispatch(
      toasterAlert({
        type: 'error',
        title: 'Error',
        message: e?.response?.data?.hint || 'Failed to refresh attendant user summary.'
      })
    )
  );
}

const normalizeDateFilters = (inputFilter: any, dateFields: string[]): any => {
  const dateFieldSet = new Set(dateFields);

  const toDateOrNull = (val: any): Date | null => {
    if (val == null || val === '') return null;
    if (val instanceof Date) return isNaN(val.getTime()) ? null : val;
    const d = new Date(val);
    return isNaN(d.getTime()) ? null : d;
  };

  // Use local day boundaries so date-equals covers the selected local day.
  const startOfDayLocal = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
  const nextDayLocal = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1, 0, 0, 0, 0);

  const transform = (f: any): any => {
    if (!f) return f;
    if (f.filters && Array.isArray(f.filters)) {
      return {
        ...f,
        filters: f.filters.map(transform)
      };
    }

    if (f.field && dateFieldSet.has(f.field)) {
      const valueAsDate = toDateOrNull(f.value);
      if (!valueAsDate) {
        return { ...f, value: null };
      }

      if (f.operator === 'eq') {
        const start = startOfDayLocal(valueAsDate);
        const end = nextDayLocal(valueAsDate);
        return {
          logic: 'and',
          filters: [
            { field: f.field, operator: 'gte', value: start },
            { field: f.field, operator: 'lt', value: end }
          ]
        };
      }

      return { ...f, value: valueAsDate };
    }

    return f;
  };

  return transform(inputFilter);
}

/** Billing grids: sort, normalize date filters, optional groupBy, then page. Returns total for Kendo pagination. */
const processWithGroups = (
  data: any[],
  group: GroupDescriptor[],
  filter: any,
  sort: any,
  skip: number,
  take: number,
  dateFields: string[]
): { processedData: any[]; totalRecords: number } => {
  let newData = [...data];

  if (sort && sort.length > 0) {
    newData = orderBy(newData, sort);
  }

  if (filter) {
    const normalized = normalizeDateFilters(filter, dateFields);
    newData = filterBy(newData, normalized);
  }

  if (group && group.length > 0) {
    const groupedData = groupBy(newData, group);
    const totalRecords = groupedData.length;
    const processedData = groupedData.slice(skip, skip + take);
    return { processedData, totalRecords };
  }

  const totalRecords = newData.length;
  const processedData = newData.slice(skip, skip + take);
  return { processedData, totalRecords };
};

/** Kendo grid: sort + filter + page (no grouping). Use `totalRecords` for server-style pagination total. */
const processData = (
  data: any[],
  filter: any,
  sort: any,
  skip: number,
  take: number
): { processedData: any[]; totalRecords: number } => {
  let newData = [...data];

  if (sort && sort.length > 0) {
    newData = orderBy(newData, sort);
  }

  if (filter) {
    newData = filterBy(newData, filter);
  }

  const totalRecords = newData.length;
  const processedData = newData.slice(skip, skip + take);
  return { processedData, totalRecords };
};

export { getUserToken, logOut, getAttendantUserSummary, normalizeDateFilters, processWithGroups, processData }
